// Minimal invoice layout using @react-pdf/renderer
// Receives a single canonical InvoiceDisplayModel — no tax math inside this component.
import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { fmtInr, numberToWords } from "./invoiceUtils";

const MinimalLayout = ({ model }) => {
  const {
    invoice_number,
    invoice_date,
    notes,
    terms,
    items,
    subtotal,
    discountAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    shipping,
    roundoff,
    total,
    organization,
    customer,
    template,
  } = model || {};

  const t = template || {};
  
  // Get item columns from template, default to minimal columns
  const itemColumns = Array.isArray(t.item_columns) 
    ? t.item_columns 
    : ["item_name", "quantity", "price", "amount"];

  const styles = StyleSheet.create({
    page: {
      padding: 60,
      fontFamily: "Helvetica",
      fontSize: 10,
    },
    header: {
      marginBottom: 40,
    },
    companyName: {
      fontSize: 28,
      fontWeight: "light",
      color: "#000",
      marginBottom: 8,
    },
    invoiceInfo: {
      fontSize: 12,
      color: "#666",
    },
    billTo: {
      marginBottom: 32,
    },
    sectionTitle: {
      fontSize: 10,
      fontWeight: "bold",
      color: "#000",
      marginBottom: 8,
    },
    customerInfo: {
      fontSize: 12,
      color: "#333",
    },
    items: {
      marginBottom: 32,
    },
    itemRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      padding: "12 0",
      borderBottomWidth: 1,
      borderBottomColor: "#eee",
    },
    itemName: {
      fontWeight: "bold",
    },
    itemDetails: {
      fontSize: 10,
      color: "#666",
    },
    summary: {
      alignSelf: "flex-end",
      width: 220,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      padding: "8 0",
      borderBottomWidth: 1,
      borderBottomColor: "#eee",
    },
    summaryTotal: {
      flexDirection: "row",
      justifyContent: "space-between",
      padding: "12 0",
      fontWeight: "bold",
      fontSize: 14,
    },
    amountWords: {
      marginTop: 32,
      fontSize: 10,
      color: "#666",
      fontStyle: "italic",
    },
    footer: {
      marginTop: 48,
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: "#eee",
      fontSize: 9,
      color: "#999",
      textAlign: "center",
    },
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.companyName}>
            {organization?.organization_name || "Your Business"}
          </Text>
          <Text style={styles.invoiceInfo}>
            {invoice_number} · {invoice_date}
          </Text>
        </View>

        {/* Bill To */}
        <View style={styles.billTo}>
          <Text style={styles.sectionTitle}>BILL TO</Text>
          <View style={styles.customerInfo}>
            <Text>{customer?.name || "-"}</Text>
            {customer?.address && <Text>{customer.address}</Text>}
            {customer?.email && <Text>{customer.email}</Text>}
          </View>
        </View>

        {/* Items */}
        <View style={styles.items}>
          {(items || []).map((item, idx) => (
            <View key={item.id || idx} style={styles.itemRow}>
              <View style={{ marginRight: 12 }}>
                <Text style={{ fontWeight: "bold" }}>{idx + 1}.</Text>
              </View>
              <View style={{ flex: 1 }}>
                {itemColumns.map((col) => {
                  if (col === "item_name") {
                    return <Text key={col} style={styles.itemName}>{item.item_name}</Text>;
                  } else if (col === "description") {
                    return <Text key={col} style={styles.itemDetails}>{item.description}</Text>;
                  } else if (col === "quantity") {
                    return <Text key={col} style={styles.itemDetails}>Qty: {item.quantity}</Text>;
                  } else if (col === "unit") {
                    return <Text key={col} style={styles.itemDetails}>Unit: {item.unit}</Text>;
                  } else if (col === "hsn_sac") {
                    return <Text key={col} style={styles.itemDetails}>HSN: {item.hsn_sac}</Text>;
                  } else if (col === "price") {
                    return <Text key={col} style={styles.itemDetails}>Rate: {fmtInr(item.price)}</Text>;
                  }
                  return null;
                })}
              </View>
              {itemColumns.includes("amount") && (
                <Text style={{ fontWeight: "bold" }}>
                  {fmtInr(item.amount)}
                </Text>
              )}
            </View>
          ))}
        </View>

        {/* Summary */}
        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text>Subtotal</Text>
            <Text>{fmtInr(subtotal)}</Text>
          </View>
          {discountAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text>Discount</Text>
              <Text>-{fmtInr(discountAmount)}</Text>
            </View>
          )}
          {cgstAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text>CGST</Text>
              <Text>{fmtInr(cgstAmount)}</Text>
            </View>
          )}
          {sgstAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text>SGST</Text>
              <Text>{fmtInr(sgstAmount)}</Text>
            </View>
          )}
          {igstAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text>IGST</Text>
              <Text>{fmtInr(igstAmount)}</Text>
            </View>
          )}
          {shipping > 0 && (
            <View style={styles.summaryRow}>
              <Text>Shipping</Text>
              <Text>{fmtInr(shipping)}</Text>
            </View>
          )}
          {roundoff !== 0 && Math.abs(roundoff) > 0.01 && (
            <View style={styles.summaryRow}>
              <Text>Roundoff</Text>
              <Text>
                {roundoff > 0 ? "+" : ""}
                {fmtInr(roundoff)}
              </Text>
            </View>
          )}
          <View style={styles.summaryTotal}>
            <Text>Total</Text>
            <Text>{fmtInr(total)}</Text>
          </View>
        </View>

        {/* Amount in Words */}
        <Text style={styles.amountWords}>{numberToWords(total || 0)}</Text>

        {/* Terms */}
        {t.show_terms !== false && terms && (
          <View style={{ marginTop: 32 }}>
            <Text style={styles.sectionTitle}>Terms & Conditions</Text>
            <Text style={{ fontSize: 9, lineHeight: 1.5 }}>{terms}</Text>
          </View>
        )}

        {/* Notes */}
        {t.show_notes !== false && notes && (
          <View style={{ marginTop: 16 }}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text style={{ fontSize: 9, lineHeight: 1.5 }}>{notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          {t.footer_text && <Text>{t.footer_text}</Text>}
          {t.show_powered_by !== false && <Text>Powered by BillForge</Text>}
        </View>
      </Page>
    </Document>
  );
};

MinimalLayout.displayName = "MinimalLayout";

export default MinimalLayout;
